import { useId, type InputHTMLAttributes } from "react";
import { AlertCircle } from "lucide-react";
import { Dica } from "./Dica";

/*
 * Campo de texto com rótulo, dica e erro.
 *
 * O doc 06a §5 é categórico sobre a mensagem de erro: "nunca só cor: sempre
 * ícone mais texto". Cor sozinha não chega a quem tem daltonismo nem a quem usa
 * leitor de tela, e é a informação de que o formulário não vai passar.
 *
 * Não há texto de apoio embaixo do campo, e é de propósito: a regra que o
 * rótulo não diz sozinho vai na `dica` (ver `Dica`). Um parágrafo fixo sob cada
 * campo é lido uma vez e vira ruído em toda abertura seguinte.
 */
interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  rotulo: string;
  /** Regra que o rótulo não diz sozinho ("vazio é sem limite"), em tooltip. */
  dica?: string | undefined;
  erro?: string | undefined;
  ref?: React.Ref<HTMLInputElement>;
}

export function Campo({ rotulo, dica, erro, className = "", ref, ...resto }: Props) {
  const id = useId();
  const idErro = `${id}-erro`;

  return (
    // `min-w-0`: sem ele, o tamanho intrínseco do `input` vence o `fr` da grade do
    // formulário, e a coluna estoura a margem do modal.
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="rotulo">
          {rotulo}
        </label>
        {dica !== undefined && <Dica texto={dica} />}
      </div>

      <input
        id={id}
        ref={ref}
        aria-invalid={erro !== undefined}
        // A recusa vai para o leitor de tela junto do campo, e não só na cor.
        {...(erro !== undefined ? { "aria-describedby": idErro } : {})}
        className={[
          "h-9 rounded-controle border bg-superficie-2 px-3 text-corpo text-texto",
          "placeholder:text-texto-3",
          // Travado pela janela de alteração, ou só leitura: documento e banco.
          "disabled:cursor-not-allowed disabled:opacity-60 read-only:text-texto-2",
          erro ? "border-falha" : "border-fio hover:border-fio-forte",
          className,
        ].join(" ")}
        {...resto}
      />

      {erro !== undefined && (
        <p id={idErro} role="alert" className="flex items-center gap-1 text-[12px] text-falha">
          <AlertCircle aria-hidden="true" className="size-3.5 shrink-0" />
          {erro}
        </p>
      )}
    </div>
  );
}
